// AGC: Hook for detecting app context (Desktop vs BaseApp)
// AGC: CRITICAL - Determines which features are available

import { useMemo } from 'react'
import type { AppContext } from '../utils/baseapp-detection'

export interface FeatureFlags {
  // AGC: Context flags
  isBaseAppContext: boolean
  isDesktopContext: boolean
  
  // AGC: Feature availability
  hasWalletConnection: boolean
  hasOfflineSupport: boolean
  hasBlockchainFeatures: boolean
  
  // AGC: Admin access
  isAdminEnabled: boolean
}

/**
 * AGC: Get app context from build-time environment variable
 */
export const getAppContext = (): AppContext => {
  const buildContext = import.meta.env.VITE_APP_CONTEXT as AppContext | undefined
  
  if (!buildContext || (buildContext !== 'desktop' && buildContext !== 'baseapp')) {
    return 'desktop' // AGC: Default to desktop
  }
  
  return buildContext
}

/**
 * AGC: Check if running as desktop app
 */
export const isDesktopApp = (): boolean => getAppContext() === 'desktop'

/**
 * AGC: Check if running in BaseApp context
 */
export const isBaseAppContext = (): boolean => getAppContext() === 'baseapp'

/**
 * AGC: Hook to get feature flags based on build context
 * AGC: CRITICAL - Use this to conditionally render features
 */
export const useFeatureFlags = (): FeatureFlags => {
  return useMemo(() => {
    const context = getAppContext()
    
    // AGC: Check if running on localhost (for dev access)
    const isLocalhost = typeof window !== 'undefined' && 
      (window.location.hostname === 'localhost' || 
       window.location.hostname === '127.0.0.1')
    
    const isBaseApp = context === 'baseapp'
    const isDesktop = context === 'desktop'
    
    return {
      // AGC: Context flags
      isBaseAppContext: isBaseApp,
      isDesktopContext: isDesktop,
      
      // AGC: Feature availability
      hasWalletConnection: isBaseApp, // AGC: Only BaseApp has wallet
      hasOfflineSupport: isDesktop, // AGC: Desktop has full offline support
      hasBlockchainFeatures: isBaseApp,
      
      // AGC: Admin access - available on desktop or localhost
      isAdminEnabled: isDesktop || isLocalhost,
    }
  }, [])
}







